import { supabase } from '../lib/supabase';

export interface ProjectPhoto {
  id: string;
  project_id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface UploadProjectPhotoParams {
  projectId: string;
  file: File;
}

export interface UploadProjectPhotoResult {
  success: boolean;
  photo?: ProjectPhoto;
  error?: string;
}

/**
 * Upload a photo for a project
 */
export async function uploadProjectPhoto({
  projectId,
  file
}: UploadProjectPhotoParams): Promise<UploadProjectPhotoResult> {
  try {
    console.log('📸 Starting project photo upload:', { projectId, fileName: file.name });

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `project-photos/${projectId}/${fileName}`;

    console.log('📸 Generated file path:', filePath);

    // Upload file to Supabase storage
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('user-uploads') // Use the established user-uploads bucket
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      console.error('❌ Storage upload error:', uploadError);
      return { success: false, error: `Upload failed: ${uploadError.message}` };
    }

    console.log('✅ File uploaded to storage:', uploadData.path);

    // Get current user for uploaded_by
    const { data: { user } } = await supabase.auth.getUser();

    // Save photo record to database
    console.log('📸 Inserting project photo record:', { 
      projectId, 
      fileName: file.name 
    });

    const { data: photoData, error: dbError } = await supabase
      .from('project_photos')
      .insert({
        project_id: projectId,
        file_name: file.name,
        file_path: filePath,
        file_size: file.size,
        mime_type: file.type,
        uploaded_by: user?.id || null
      })
      .select()
      .single();

    if (dbError) {
      console.error('❌ Database insert error:', dbError);
      
      // Clean up uploaded file if database insert fails
      await supabase.storage
        .from('user-uploads')
        .remove([filePath]);
      
      return { success: false, error: `Database error: ${dbError.message}` };
    }

    console.log('✅ Project photo record saved to database:', photoData.id);

    return { success: true, photo: photoData };
  } catch (error) {
    console.error('❌ Unexpected error uploading project photo:', error);
    return { success: false, error: 'Unexpected error occurred' };
  }
}

/**
 * Get all photos for a project
 */
export async function getProjectPhotos(projectId: string): Promise<ProjectPhoto[]> {
  try {
    console.log('📸 Fetching photos for project:', projectId);

    const { data, error } = await supabase
      .from('project_photos')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching project photos:', error);
      return [];
    }

    console.log('✅ Found project photos:', data?.length || 0);
    return data || [];
  } catch (error) {
    console.error('❌ Unexpected error fetching project photos:', error);
    return [];
  }
}

/**
 * Delete a project photo
 */
export async function deleteProjectPhoto(photoId: string): Promise<boolean> {
  try {
    console.log('📸 Deleting project photo:', photoId);

    // First get the photo record to get the file path
    const { data: photo, error: fetchError } = await supabase
      .from('project_photos')
      .select('file_path')
      .eq('id', photoId)
      .single();

    if (fetchError || !photo) {
      console.error('❌ Error fetching project photo for deletion:', fetchError);
      return false;
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('user-uploads')
      .remove([photo.file_path]);

    if (storageError) {
      console.error('❌ Error deleting from storage:', storageError);
      // Continue with database deletion even if storage deletion fails
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('project_photos')
      .delete()
      .eq('id', photoId);

    if (dbError) {
      console.error('❌ Error deleting from database:', dbError);
      return false;
    }

    console.log('✅ Project photo deleted successfully');
    return true;
  } catch (error) {
    console.error('❌ Unexpected error deleting project photo:', error);
    return false;
  }
}

/**
 * Get public URL for a project photo
 */
export function getProjectPhotoUrl(filePath: string): string {
  const { data } = supabase.storage
    .from('user-uploads')
    .getPublicUrl(filePath);
  
  return data.publicUrl;
}
